import React, { useEffect, useState } from "react";
import StrapiService from "./StrapiService.js";
import "./listarticles.css";
import { useRecoilValue } from "recoil";
import { refreshListArticle } from "../../service/memory/recoil/listarticle";
import GenericCardComponent from "../generic/elements/card/GenericCardComponent.jsx";
import { Spin, notification } from "antd";
import { getFullName } from "../careprovider/visits/utils.js";
import ReactMarkdown from "react-markdown";
import NoDataMessageComponent from "../../components/generic/nodatamessage/NoDataMessageComponent.jsx";
import IntentType from "../../intentexecutor/utils/IntentType.js";
import { getApiUrl } from "../../service/network/UrlMapper.js";
import { performPostRequest } from "../../service/network/NetworkService.js";
import ErrorNotificationComponent from "../../components/generic/error/ErrorNotificationComponent.jsx";
import ConfigurationConstants from "../utils/configurationconstants.js";
import { currentConfiguration } from "../../service/memory/recoil/configurationstore.js";

const ListArticles = ({
  type,
  actions,
  userIdentifier,
  teams,
  organizationIdentifier,
}) => {
  const strapiService = new StrapiService();
  const [posts, setPosts] = useState([]);
  const [postsLoading, SetPostsLoading] = useState();
  const [genericComponentData, setGenericComponentData] = useState([]);
  const refreshArticle = useRecoilValue(refreshListArticle);

  const actionIcons = actions?.actionIcons ? actions?.actionIcons : [];
  const primaryActions = actions?.primaryActions ? actions?.primaryActions : [];
  const secondaryActions = actions?.secondaryActions
    ? actions?.secondaryActions
    : [];

  const [workforceDetails, setWorkforceDetails] = useState({});
  const [patientDetails, setPatientDetails] = useState({});
  const configuration = useRecoilValue(currentConfiguration);
  const secretKey =
    configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY];

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const month = monthNames[date.getMonth()];
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear());
    return `${month} ${day}, ${year}`;
  };

  useEffect(() => {
    const getAllData = async () => {
      SetPostsLoading(true);
      try {
        const alldata = await strapiService.getArticles(
          organizationIdentifier,
          type,
          userIdentifier,
          teams
        );

        const articles = alldata?.data?.filter((article) => {
          const articleTeams = article?.attributes?.teams || [];
          if (!articleTeams || articleTeams.length === 0) {
            return true;
          }
          return teams?.some((teamId) => articleTeams?.includes(teamId));
        });

        setPosts( type === "Journal" ? alldata?.data?.reverse() : articles?.reverse());
      } catch (error) {
        notification.error({
          message: "Error while fetching data",
          placement: "bottomRight",
        });
      } finally {
        SetPostsLoading(false);
      }
    };

    getAllData();
  }, [organizationIdentifier, refreshArticle, teams, type, userIdentifier]);

  useEffect(() => {
    const filterData = (postList) => {
      const filteredArray = postList.map((post) => {
        const workforce =
          workforceDetails[`${post?.attributes?.userIdentifier}`];
        const workforceName = getFullName(
          workforce?.givenName,
          workforce?.familyName
        );
        const patient = patientDetails[post?.attributes?.patientIdentifier];
        const patientName = getFullName(
          patient?.givenName,
          patient?.familyName
        );
        return {
          primaryHeading: <div>{post.attributes.topic}</div>,
          secondaryHeading: (
            <div>Written by {workforceName || post?.attributes?.author}</div>
          ),
          description: (
            <ReactMarkdown className="markdown">
              {post.attributes.body}
            </ReactMarkdown>
          ),
          additionalData: {
            date: formatDate(post?.attributes?.createdAt),
            ...(patientName && { patientName }),
          },
          actionIcons: actionIcons,
          primaryActions: primaryActions,
          secondaryActions: secondaryActions,
          actionData: post,
          showIconsForAdditionalData: true,
        };
      });
      setGenericComponentData(filteredArray);
    };

    if (Object.keys(workforceDetails)?.length) {
      filterData(posts);
    }
  }, [posts, workforceDetails]);

  useEffect(() => {
    const getWorkforceDetails = async () => {
      const workforceItdentifiersList = posts
        ?.map((item) => item?.attributes?.userIdentifier)
        .filter(Boolean);

      const payload = {
        intent: IntentType.GET_WORKFORCE_DETAILS_MAP_BY_USER_IDENTIFIER,
        payload: {
          userIdentifiers: workforceItdentifiersList,
        },
        context: {
          organizationIdentifier,
        },
      };
      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_WORKFORCE_DETAILS_MAP_BY_USER_IDENTIFIER],
          JSON.stringify(payload)
        );
        const resultJson = await result.json();
        setWorkforceDetails(resultJson);
      } catch (error) {
        ErrorNotificationComponent(error);
      }
    };

    if (posts.length) {
      getWorkforceDetails();
    }
  }, [organizationIdentifier, posts]);

  useEffect(() => {
    const getPatientDetails = async () => {
      const patientItdentifiersList = posts
        .map((item) => item?.attributes?.patientIdentifier)
        .filter(Boolean);

      const payload = {
        intent: IntentType.GET_PATIENT_DETAILS_MAP_BY_USER_IDENTIFIER,
        payload: {
          userIdentifiers: patientItdentifiersList,
          secretKey: secretKey?.key,
        },
        context: {
          teamIds: teams,
          organizationIdentifier,
          userIdentifier,
        },
      };
      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_PATIENT_DETAILS_MAP_BY_USER_IDENTIFIER],
          JSON.stringify(payload)
        );
        const resultJson = await result.json();
        setPatientDetails(resultJson);
      } catch (error) {
        ErrorNotificationComponent(error);
      }
    };

    if (type === "Journal") {
      getPatientDetails();
    }
  }, [
    organizationIdentifier,
    posts,
    secretKey?.key,
    teams,
    type,
    userIdentifier,
  ]);

  return (
    <div className="article-list-section">
      {postsLoading ? (
        <div style={{ height: "65vh", display: "flex", alignItems: "center" }}>
          <Spin
            size="medium"
            className="d-flex justify-content-center m-auto"
          />
        </div>
      ) : posts?.length ? (
        <div>
          {genericComponentData.map((userData, index) => (
            <GenericCardComponent data={userData} key={index} />
          ))}
        </div>
      ) : (
        <NoDataMessageComponent
          text={`No ${type.toLowerCase()} entries available!`}
        />
      )}
    </div>
  );
};

export default ListArticles;
